import React from 'react';

import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {JSONValue} from 'util/Types';

import {AnalyticsParameterConfigGraphQL} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import AnalyticsParameterFieldError from './AnalyticsParameterFieldError';
import AnalyticsParameterValueEditor, {InputProps} from './AnalyticsParameterValueEditor';
import {getTypesafeParameterTypeAndConfigSettings} from './data/getParameterTypeAndConfigSetting';
import {ParameterTypeToTSType} from './data/ValueValidators';
import {PARAMETER_TYPE_TO_READABLE} from './ParameterConstants';
import ParameterTypeHelpButton from './ParameterTypeHelpButton';
import {ParameterType} from './ParameterTypes';
import useAnalyticsParameterValueAndValidator from './useAnalyticsParameterValueAndValidator';

export interface AnalyticsParameterValueModalProps {
  config: AnalyticsParameterConfigGraphQL;
  onRequestClose: () => void;
  onRequestSubmit: (result: {valueJson: JSONValue}) => void;
  initialValueJson: JSONValue;
}

export default function AnalyticsParameterValueModal<T extends ParameterType>(
  props: AnalyticsParameterValueModalProps
): JSX.Element {
  const {type, configSetting} = getTypesafeParameterTypeAndConfigSettings<T>(props.config);
  const paramConfigSetting = configSetting;

  const {valueJson, valueJsonIsValid, updateValueJson} = useAnalyticsParameterValueAndValidator(
    type,
    props.initialValueJson
  );

  function handleSubmit(): void {
    props.onRequestSubmit({valueJson});
  }

  const inputProps: InputProps<T> = {
    valueJson: valueJson as ParameterTypeToTSType[T],
    paramConfigSetting,
    onChange: updateValueJson,
    allowAutoFocus: true,
  };

  return (
    <Modal
      buttons={[
        {
          disabled: !valueJsonIsValid,
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: 'Update',
        },
      ]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      title={`Edit ${props.config.systemName} value`}
      scrollable={false}
      expandHeight={false}
    >
      <FormGroup>
        <FieldLabel label={`Value (${PARAMETER_TYPE_TO_READABLE[type]})`} />
        <ParameterTypeHelpButton className="u-mini-spacing-left" />
        <AnalyticsParameterValueEditor paramType={type} inputProps={inputProps} />
        {!valueJsonIsValid && <AnalyticsParameterFieldError type={type} />}
      </FormGroup>
    </Modal>
  );
}
